import React, { useCallback } from "react";

import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import { EntitiesSupportingPreview } from "@mapmycustomers/shared/types/entity";
import { isMapped } from "@mapmycustomers/shared/util/assert";

import childCompanyImage from "@app/assets/associations/childCompany.svg";
import companyImage from "@app/assets/associations/company.svg";
import dealImage from "@app/assets/associations/deal.svg";
import personImage from "@app/assets/associations/person.svg";
import routeImage from "@app/assets/associations/route.svg";
import RelationshipType, {
  getRelationshipAddMessage,
} from "@app/component/preview/enum/RelationshipType";

import styles from "./NoResult.module.scss";

const relationShipTypeImage: Record<RelationshipType, string> = {
  [RelationshipType.CHILD_COMPANY]: childCompanyImage,
  [RelationshipType.DEALS]: dealImage,
  [RelationshipType.PARENT_COMPANY]: companyImage,
  [RelationshipType.PEOPLE]: personImage,
  [RelationshipType.ROUTES]: routeImage,
};

interface Props {
  entity: EntitiesSupportingPreview;
  onAdd: (type: RelationshipType) => void;
  supportedRelationshipTypes: RelationshipType[];
}

const NoResult: React.FC<Props> = ({ entity, onAdd, supportedRelationshipTypes }) => {
  const intl = useIntl();
  const hasLocation = isMapped(entity);

  const isDisabled = useCallback(
    (type: RelationshipType) => !hasLocation && type === RelationshipType.ROUTES,
    [hasLocation]
  );

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        {intl.formatMessage({
          id: "entityPreview.relationships.noResult.title",
          defaultMessage: "No results",
          description: "Entity preview relationships - no result - title",
        })}
      </div>
      <div className={styles.description}>
        {intl.formatMessage({
          id: "entityPreview.relationships.noResult.description",
          defaultMessage: "There are no relationships that match this search.",
          description: "Entity preview relationships - no result - description",
        })}
      </div>
      <div className={styles.cardContainer}>
        {supportedRelationshipTypes.map((type) => (
          <Tooltip
            key={type}
            title={
              isDisabled(type)
                ? intl.formatMessage({
                    id: "entityPreview.relationships.noResult.disabledAction",
                    defaultMessage:
                      "Unable to add due to invalid address. Please update the record’s location to add it to a route.",
                    description:
                      "Tooltip for disabled Add to route button on a Record pane - no result",
                  })
                : null
            }
          >
            <div
              className={cn(styles.card, { [styles.cardDisabled]: isDisabled(type) })}
              key={type}
              onClick={() => !isDisabled(type) && onAdd(type)}
            >
              <img
                alt={`add type ${type}`}
                className={cn(styles.image, { [styles.imageDisabled]: isDisabled(type) })}
                src={relationShipTypeImage[type]}
              />
              <div className={cn(styles.addText, { [styles.addTextDisabled]: isDisabled(type) })}>
                {getRelationshipAddMessage(intl, type, entity.entity)}
              </div>
            </div>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

export default NoResult;
